import { useCallback } from 'react';
import { omit } from 'remeda';

import { DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS } from '../../config';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { debounce } from '../../utils';
import type { DataChannelAppStateMessage, DataChannelMessageSender } from '../../video-sdk';
import {
  AudioRecordingState,
  DataChannelMessageType,
  VideoPlatform,
  dataChannelAppStateAtom,
  useSendMessage,
} from '../../video-sdk';

const sendDebouncedLoggingEvents = debounce(sendLoggingEvents, DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS);

function toLegacyRecordingProperty(
  recordingState: DataChannelAppStateMessage['participantState']['recordingState'],
): DataChannelAppStateMessage['participantState']['isRecording'] {
  return {
    value: recordingState.value === AudioRecordingState.RECORDING,
    lastUpdated: recordingState.lastUpdated,
  };
}

export const useSendParticipantStateCallback = () => {
  const getDataTrackState = useAtomCallbackMemoized((get) => get(dataChannelAppStateAtom), []);
  const sendMessage = useSendMessage();
  const getVideoPlatform = useVideoPlatformCallback();

  return useCallback(
    async (senderIdentity: DataChannelMessageSender) => {
      const participantState = {
        ...getDataTrackState(),
        isWhiteboardMode: { value: false, lastUpdated: 0 },
      };
      const formattedMessage: DataChannelAppStateMessage = {
        participantState:
          getVideoPlatform() === VideoPlatform.Zoom
            ? omit(participantState, ['isRecording'])
            : {
                ...participantState,
                isRecording: toLegacyRecordingProperty(participantState.recordingState),
              },
        sender: senderIdentity,
        type: DataChannelMessageType.APP_STATE,
      };
      const didSendMessage = await sendMessage(formattedMessage);
      if (didSendMessage) {
        sendDebouncedLoggingEvents(
          TrackingEvents.REMOTE_DATA_TRACK_MESSAGE_SENT,
          {
            // TODO: TREE-455 - Remove
            isRecording: participantState.isRecording?.value,
            patientConsent: participantState.patient.consent,
            providerConsent: participantState.provider.consent,
            recordingState: participantState.recordingState,
          },
          {
            logLevel: 'info',
            message: 'Local participant sent a message on the data track (DEBOUNCED)',
          },
        );
      }
    },
    [getDataTrackState, getVideoPlatform, sendMessage],
  );
};
